/**
 * Canvas decode-draw-encode core — the iOS-proven byte shape has ONE home
 * (extracted from contactUtils.imageUrlToBase64 + chatImageUpload.compressToBlob,
 * which carried drifting copies — _refactor.md row 2026-07-19).
 *
 * Draw a decoded image onto a canvas scaled so its longest side is
 * `maxDimension`. Downscale-only by default (chat/post uploads); pass
 * `upscale: true` to also enlarge small sources (vCard photos — a 200px
 * source renders too small in the iOS contact poster).
 */
export const drawScaledCanvas = (img, maxDimension, { upscale = false, region } = {}) => {
  const w = img.naturalWidth || img.width;
  const h = img.naturalHeight || img.height;
  // Optional source crop as 0–1 fractions [x, y, w, h] (face detection's
  // zoomed tiles); default is the whole image.
  const [rx, ry, rw, rh] = region || [0, 0, 1, 1];
  const sw = w * rw;
  const sh = h * rh;
  const longest = Math.max(sw, sh);
  const scale = upscale ? maxDimension / longest : Math.min(1, maxDimension / longest);
  const width = Math.max(1, Math.round(sw * scale));
  const height = Math.max(1, Math.round(sh * scale));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  // No region → the original 5-argument draw, byte-identical to every upload
  // path before regions existed (the source-rect form is only for tiles).
  if (region) ctx.drawImage(img, rx * w, ry * h, sw, sh, 0, 0, width, height);
  else ctx.drawImage(img, 0, 0, width, height);
  return canvas;
};

/**
 * Compress a picked image File to a JPEG data URL via drawScaledCanvas —
 * the profile-photo byte shape (200px longest side @ 0.8 by default). Folded
 * here from PhotoSelectionOverlay's hand-rolled copy (tiered-exchange Step 5;
 * the QR Customize sheet is the second caller — one home, no drift).
 *
 * @param {File} file - image file from an <input type="file">
 * @param {Object} [opts]
 * @param {number} [opts.maxDimension=200]
 * @param {number} [opts.quality=0.8]
 * @returns {Promise<string>} JPEG data URL
 */
export const compressImageFile = (file, { maxDimension = 200, quality = 0.8 } = {}) =>
  loadImageFile(file).then((img) =>
    drawScaledCanvas(img, maxDimension).toDataURL('image/jpeg', quality)
  );

/**
 * Decode a picked image File into a loaded <img> — the decode half of
 * compressImageFile, shared with photo-meta capture (face detection reads
 * the same decoded image the upload is compressed from).
 *
 * @param {File} file
 * @returns {Promise<HTMLImageElement>}
 */
export const loadImageFile = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
